import React, { useEffect, useMemo } from 'react';
import FlagProvider, { useUnleashContext } from '@unleash/proxy-client-react';

import { useAuthStore } from '@/modules/user/stores';

const unleashConfig = {
  url: window.REACT_APP_UNLEASH_API_URL || import.meta.env.REACT_APP_UNLEASH_API_URL || '',
  clientKey: window.REACT_APP_UNLEASH_API_TOKEN || import.meta.env.REACT_APP_UNLEASH_API_TOKEN || '',
  // Set to 0 means flags will be fetched only on login or page refresh
  refreshInterval: 0,
  appName: 'user-fe',
};

const buildUnleashUserId = (id: string) => `employee_${id}`;

interface FeatureFlagContextUpdaterProps {
  children: React.ReactNode;
}

const FeatureFlagContextUpdater: React.FC<FeatureFlagContextUpdaterProps> = ({ children }) => {
  const { user } = useAuthStore();
  const updateContext = useUnleashContext();

  const unleashUserId = useMemo(() => (user?.id ? buildUnleashUserId(user.id) : undefined), [user?.id]);

  useEffect(() => {
    if (unleashUserId) {
      updateContext({
        userId: unleashUserId,
        properties: {
          username: user?.username || '',
          organizationId: user?.organization?.id || '',
          organizationPath: user?.organization?.path || '',
          employeeId: user?.employee?.id || '',
        },
      });
    }
  }, [
    unleashUserId,
    user?.username,
    user?.organization?.id,
    user?.organization?.path,
    user?.employee?.id,
    updateContext,
  ]);

  return <>{children}</>;
};

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  if (!unleashConfig.url) {
    return children;
  }
  return (
    <FlagProvider config={unleashConfig}>
      <FeatureFlagContextUpdater>{children}</FeatureFlagContextUpdater>
    </FlagProvider>
  );
};

export default FeatureFlagProvider;
